/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";
import AddIcon from "@material-ui/icons/Add";
import Modal from "@material-ui/core/Modal";
import Box from "@material-ui/core/Box";
import TablePagination from "@material-ui/core/TablePagination";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Menu from "@material-ui/core/Menu";
// core components
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Table from "components/Table/Table.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import TrainingNote from "components/TrainingNote/TrainingNote.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import {
  getTrainingNotes,
  getTrainingNoteConfig,
  createTrainingNote,
  updateTrainingNote,
  uploadTrainingNotesCSV
} from "../../services/userDataProvider";
import {
  prefersDarkModeMediaQuery,
  darkModeStyleBlocks
} from "assets/jss/material-dashboard-react.jsx";

const styles = theme => ({
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0"
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF"
    }
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1"
    }
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  addButton: {
    minWidth: "40px",
    padding: "8px"
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)"
  },
  modalBox: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    width: "80%",
    height: "80%",
    overflow: "auto",
    padding: "20px",
    position: "relative"
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #eee"
  },
  formField: {
    marginBottom: "20px"
  },
  scaleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "10px"
  },
  scaleLabel: {
    fontSize: "12px",
    color: "#666"
  },
  tableCell: {
    "&:nth-child(1)": {
      width: "10%"
    },
    "&:nth-child(2)": {
      width: "30%"
    },
    "&:nth-child(3)": {
      width: "20%"
    },
    "&:nth-child(4)": {
      width: "40%"
    }
  },
  headerButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center"
  },
  [prefersDarkModeMediaQuery]: darkModeStyleBlocks.trainingNotes
});

class TrainingNotes extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      notes: [],
      modalOpen: false,
      loading: true,
      currentPage: 1,
      totalPages: 1,
      config: null,
      saving: false,
      saveMessage: "",
      saveError: "",
      viewMode: "create", // "create", "view", or "edit"
      selectedNote: null,
      formData: {
        title: "",
        activity: "",
        notes: "",
        date: new Date().toISOString().split("T")[0], // Current date as default
        answers: {}
      },
      pageSize: 10,
      totalCount: 0,
      actionsMenuAnchor: null,
      uploading: false,
      csvModalOpen: false,
      selectedFile: null,
      csvPreview: null,
      csvColumnsValid: false,
      exportModalOpen: false,
      exportStartDate: "",
      exportEndDate: "",
      exporting: false
    };
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.loadTrainingNotes();
    this.loadNoteConfig();
  }

  loadNoteConfig = async () => {
    try {
      const { user } = this.props;
      const userId = user && user.id;
      const config = await getTrainingNoteConfig(this.props.axios, userId);
      this.setState({ config });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load training note config:", error);
    }
  };

  loadTrainingNotes = async () => {
    try {
      this.setState({ loading: true });
      const { user } = this.props;
      const userId = user && user.id;

      const response = await getTrainingNotes(
        this.props.axios,
        userId,
        this.state.currentPage,
        this.state.pageSize
      );

      this.setState({
        notes:
          response.results.map(note => {
            return {
              ...note,
              answers: (note.metadata && note.metadata.answers) || []
            };
          }) || [],
        totalPages: Math.ceil((response.count || 0) / 10),
        totalCount: response.count || 0,
        loading: false
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load training notes:", error);
      this.setState({ loading: false });
    }
  };

  handleViewNote = note => {
    this.setState({
      modalOpen: true,
      viewMode: "view",
      selectedNote: note,
      saveMessage: "",
      saveError: "",
      formData: {
        title: note.title || "",
        activity: note.activity || "",
        notes: note.notes || "",
        date:
          note.date || (note.created_at && note.created_at.split("T")[0]) || "",
        answers: note.answers || {}
      }
    });
  };

  handleToggleEditMode = () => {
    this.setState({
      viewMode: this.state.viewMode === "view" ? "edit" : "view"
    });
  };

  handleAddNote = async () => {
    this.setState({
      modalOpen: true,
      viewMode: "create",
      selectedNote: null,
      saveMessage: "",
      saveError: ""
    });
  };

  handleCloseModal = () => {
    this.setState({
      modalOpen: false,
      saveMessage: "",
      saveError: "",
      viewMode: "create",
      selectedNote: null,
      formData: {
        title: "",
        activity: "",
        notes: "",
        date: new Date().toISOString().split("T")[0],
        answers: {}
      }
    });
  };

  handleSaveNote = async (formData = null) => {
    const { viewMode, selectedNote } = this.state;
    const { user } = this.props;
    
    // Use provided formData or fallback to state
    const dataToSave = formData || this.state.formData;

    // Basic validation
    if (!dataToSave.title || !dataToSave.activity) {
      this.setState({
        saveError: "Please fill in required fields (Title and Activity)"
      });
      return;
    }

    this.setState({ saving: true, saveError: "", saveMessage: "" });

    try {
      const payload = {
        title: dataToSave.title,
        activity_id: dataToSave.activity,
        notes: dataToSave.notes,
        date: dataToSave.date,
        user_id: user && user.id,
        answers: dataToSave.answers
      };

      if (viewMode === "edit" && selectedNote) {
        await updateTrainingNote(this.props.axios, selectedNote.id, payload);
        this.setState({
          saving: false,
          saveMessage: "Training note updated successfully!"
        });
      } else {
        await createTrainingNote(this.props.axios, payload);
        this.setState({
          saving: false,
          saveMessage: "Training note saved successfully!"
        });
      }

      // Reload notes and close modal after 2 seconds
      setTimeout(() => {
        this.loadTrainingNotes();
        this.handleCloseModal();
      }, 2000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save training note:", error);
      this.setState({
        saving: false,
        saveError: "Failed to save training note. Please try again."
      });
    }
  };

  handleInputChange = (field, value) => {
    this.setState({
      formData: {
        ...this.state.formData,
        [field]: value
      }
    });
  };

  setFormFields = (field1, value1, field2, value2) => {
    this.setState({
      formData: {
        ...this.state.formData,
        [field1]: value1,
        [field2]: value2
      }
    });
  };

  handleAnswerChange = (questionKey, value) => {
    this.setState({
      formData: {
        ...this.state.formData,
        answers: {
          ...this.state.formData.answers,
          [questionKey]: value
        }
      }
    });
  };

  handleFormDataChange = formData => {
    this.setState({ formData });
  };

  handleChangePage = (event, newPage) => {
    this.setState({ currentPage: newPage + 1 }, () => {
      this.loadTrainingNotes();
    });
  };

  handleChangeRowsPerPage = event => {
    const newPageSize = parseInt(event.target.value, 10);

    this.setState(
      {
        pageSize: newPageSize,
        currentPage: 1
      },
      () => {
        this.loadTrainingNotes();
      }
    );
  };

  handleActionsMenuOpen = event => {
    this.setState({ actionsMenuAnchor: event.currentTarget });
  };

  handleActionsMenuClose = () => {
    this.setState({ actionsMenuAnchor: null });
  };

  handleImportCSV = () => {
    this.handleActionsMenuClose();
    this.setState({ csvModalOpen: true });
  };

  handleCloseCsvModal = () => {
    this.setState({
      csvModalOpen: false,
      selectedFile: null,
      csvPreview: null,
      csvColumnsValid: false
    });
  };

  handleFileSelect = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  handleFileChange = async event => {
    const file = event.target.files[0];
    if (file && file.type === "text/csv") {
      this.setState({ selectedFile: file });
      this.previewCSV(file);
    } else {
      alert("Please select a valid CSV file.");
    }
    // Reset file input
    event.target.value = "";
  };

  previewCSV = file => {
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target.result;
      const lines = text.split("\n").slice(0, 11); // First 10 lines + header
      const preview = lines.map(line => line.split(","));

      if (preview.length > 0) {
        const headers = preview[0].map(h => h.trim().toLowerCase());
        const requiredColumns = ["title", "date", "notes"];
        const hasRequiredColumns = requiredColumns.every(col =>
          headers.includes(col)
        );

        this.setState({
          csvPreview: preview,
          csvColumnsValid: hasRequiredColumns
        });
      }
    };
    reader.readAsText(file);
  };

  handleUploadCSV = async () => {
    if (!this.state.selectedFile) return;

    this.setState({ uploading: true });
    try {
      const { user } = this.props;
      const userId = user && user.id;
      await uploadTrainingNotesCSV(
        this.props.axios,
        this.state.selectedFile,
        userId
      );
      this.setState({ uploading: false });
      this.loadTrainingNotes();
      this.handleCloseCsvModal();
      alert("CSV imported successfully!");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to upload CSV:", error);
      this.setState({ uploading: false });
      alert("Failed to import CSV. Please try again.");
    }
  };

  handleExportCSV = () => {
    this.handleActionsMenuClose();
    this.setState({
      exportModalOpen: true,
      exportStartDate: "",
      exportEndDate: ""
    });
  };

  handleCloseExportModal = () => {
    this.setState({
      exportModalOpen: false,
      exportStartDate: "",
      exportEndDate: ""
    });
  };

  handleDownloadCSV = async () => {
    if (!this.state.exportStartDate || !this.state.exportEndDate) return;

    this.setState({ exporting: true });
    try {
      const { user } = this.props;
      const userId = user && user.id;

      // Get all notes within the date range
      const response = await getTrainingNotes(
        this.props.axios,
        userId,
        1,
        this.state.pageSize,
        this.state.exportStartDate,
        this.state.exportEndDate
      );

      const notes = response.results || [];

      // Convert to CSV
      const csvContent = this.convertToCSV(notes);

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `training_notes_${this.state.exportStartDate}_to_${
          this.state.exportEndDate
        }.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.setState({ exporting: false });
      this.handleCloseExportModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to export CSV:", error);
      this.setState({ exporting: false });
      alert("Failed to export CSV. Please try again.");
    }
  };

  convertToCSV = notes => {
    const headers = ["title", "date", "notes", "activity_id", "answers"];
    const csvRows = [];

    // Add headers
    csvRows.push(headers.join(","));

    // Add data rows
    notes.forEach(note => {
      const row = [
        `"${(note.title || "").replace(/"/g, '""')}"`,
        note.date || (note.created_at && note.created_at.split("T")[0]) || "",
        `"${(note.notes || "").replace(/"/g, '""')}"`,
        note.activity || "",
        `"${JSON.stringify(note.answers || {}).replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  };

  render() {
    const { classes } = this.props;
    const {
      notes,
      modalOpen,
      loading,
      viewMode,
      selectedNote,
      actionsMenuAnchor,
      csvModalOpen,
      csvPreview,
      csvColumnsValid,
      uploading,
      exportModalOpen,
      exportStartDate,
      exportEndDate,
      exporting
    } = this.state;

    if (loading) {
      return <div>Loading training notes...</div>;
    }

    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="info">
              <div className={classes.headerContainer}>
                <div>
                  <h4 className={classes.cardTitleWhite}>Training Notes</h4>
                  <p className={classes.cardCategoryWhite}>
                    Track your training progress and insights
                  </p>
                </div>
                <div className={classes.headerButtons}>
                  <Button
                    color="white"
                    className={classes.addButton}
                    onClick={this.handleAddNote}
                    justIcon
                  >
                    <AddIcon />
                  </Button>
                  <Button
                    color="white"
                    className={classes.addButton}
                    onClick={this.handleActionsMenuOpen}
                    justIcon
                  >
                    <MoreVertIcon />
                  </Button>
                  <Menu
                    anchorEl={actionsMenuAnchor}
                    open={Boolean(actionsMenuAnchor)}
                    onClose={this.handleActionsMenuClose}
                  >
                    <MenuItem onClick={this.handleImportCSV}>
                      Import CSV
                    </MenuItem>
                    <MenuItem onClick={this.handleExportCSV}>
                      Export CSV
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <Table
                tableHeaderColor="info"
                tableHead={["ID", "Title", "Date", "Notes"]}
                tableData={notes.map(note => [
                  note.id || "-",
                  <span
                    key={note.id}
                    style={{
                      cursor: "pointer",
                      color: "#1976d2",
                      textDecoration: "underline"
                    }}
                    onClick={() => this.handleViewNote(note)}
                  >
                    {note.title}
                  </span>,
                  note.date ||
                    (note.created_at && note.created_at.split("T")[0]) ||
                    "-",
                  note.notes && note.notes.length > 100
                    ? note.notes.substring(0, 100) + "..."
                    : note.notes || "-"
                ])}
                tableCellProps={{
                  className: classes.tableCell
                }}
              />
              <TablePagination
                component="div"
                count={this.state.totalCount || 0}
                page={this.state.currentPage - 1 || 0}
                onChangePage={this.handleChangePage}
                rowsPerPage={this.state.pageSize || 10}
                onChangeRowsPerPage={this.handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </CardBody>
          </Card>
        </GridItem>

        <TrainingNote
          open={modalOpen}
          onClose={this.handleCloseModal}
          viewMode={viewMode}
          selectedNote={selectedNote}
          onToggleEditMode={this.handleToggleEditMode}
          onSave={this.handleSaveNote}
          onFormDataChange={this.handleFormDataChange}
          saving={this.state.saving}
          saveError={this.state.saveError}
          saveMessage={this.state.saveMessage}
          config={this.state.config}
        />

        {/* CSV Import Modal */}
        <Modal
          open={csvModalOpen}
          onClose={null}
          className={classes.modal}
          aria-labelledby="csv-import-modal"
          aria-describedby="modal-for-csv-import"
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
        >
          <Box className={classes.modalBox}>
            <h2>Import Training Notes from CSV</h2>

            <div className={classes.formField}>
              <h4>Required CSV Format:</h4>
              <p>Your CSV file must contain the following columns:</p>
              <ul>
                <li>
                  <strong>title</strong> - Title of the training note
                </li>
                <li>
                  <strong>date</strong> - Date in YYYY-MM-DD format
                </li>
                <li>
                  <strong>notes</strong> - Training notes content
                </li>
              </ul>
              <p>
                <em>Optional columns: activity_id, answers (JSON format)</em>
              </p>
            </div>

            <div className={classes.formField}>
              <Button color="info" onClick={this.handleFileSelect}>
                Select CSV File
              </Button>
              {this.state.selectedFile && (
                <p style={{ marginTop: "10px" }}>
                  Selected: {this.state.selectedFile.name}
                </p>
              )}
            </div>

            {csvPreview && (
              <div className={classes.formField}>
                <h4>CSV Preview (First 10 rows):</h4>
                <div
                  style={{
                    maxHeight: "300px",
                    overflow: "auto",
                    border: "1px solid #ddd",
                    padding: "10px"
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {csvPreview[0] &&
                          csvPreview[0].map((header, index) => (
                            <th
                              key={index}
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                backgroundColor: "#f5f5f5"
                              }}
                            >
                              {header}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.slice(1).map((row, index) => (
                        <tr key={index}>
                          {row.map((cell, cellIndex) => (
                            <td
                              key={cellIndex}
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px"
                              }}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: "10px" }}>
                  {csvColumnsValid ? (
                    <span style={{ color: "green" }}>
                      ✓ Required columns found
                    </span>
                  ) : (
                    <span style={{ color: "red" }}>
                      ✗ Missing required columns (title, date, notes)
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className={classes.modalActions}>
              <Button color="default" onClick={this.handleCloseCsvModal}>
                Cancel
              </Button>
              <Button
                color="info"
                onClick={this.handleUploadCSV}
                disabled={!csvColumnsValid || uploading}
              >
                {uploading ? "Uploading..." : "Upload CSV"}
              </Button>
            </div>
          </Box>
        </Modal>

        {/* CSV Export Modal */}
        <Modal
          open={exportModalOpen}
          onClose={null}
          className={classes.modal}
          aria-labelledby="csv-export-modal"
          aria-describedby="modal-for-csv-export"
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
        >
          <Box className={classes.modalBox}>
            <h2>Export Training Notes to CSV</h2>

            <div className={classes.formField}>
              <TextField
                label="Start Date *"
                type="date"
                fullWidth
                className={classes.formField}
                value={exportStartDate}
                onChange={e =>
                  this.setState({ exportStartDate: e.target.value })
                }
                InputLabelProps={{
                  shrink: true
                }}
              />
            </div>

            <div className={classes.formField}>
              <TextField
                label="End Date *"
                type="date"
                fullWidth
                className={classes.formField}
                value={exportEndDate}
                onChange={e => this.setState({ exportEndDate: e.target.value })}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </div>

            <div className={classes.modalActions}>
              <Button color="default" onClick={this.handleCloseExportModal}>
                Cancel
              </Button>
              <Button
                color="info"
                onClick={this.handleDownloadCSV}
                disabled={!exportStartDate || !exportEndDate || exporting}
              >
                {exporting ? "Exporting..." : "Download CSV"}
              </Button>
            </div>
          </Box>
        </Modal>

        {/* Hidden file input for CSV upload */}
        <input
          type="file"
          accept=".csv"
          ref={this.fileInputRef}
          style={{ display: "none" }}
          onChange={this.handleFileChange}
        />
      </GridContainer>
    );
  }
}

TrainingNotes.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(TrainingNotes);
